<template>
  <div class="workflow-steps">
    <div v-if="!steps.length" class="workflow-empty">No steps in this group yet.</div>
    <article v-for="(step, index) in steps" :key="step.id || index" class="workflow-step">
      <header class="workflow-step-head">
        <span class="workflow-step-index">{{ index + 1 }}</span>
        <select v-model="step.type" class="workflow-select" @change="onTypeChange(step)">
          <option v-for="option in stepTypes" :key="option.value" :value="option.value">{{ option.label }}</option>
        </select>
        <input v-model="step.title" class="workflow-input workflow-step-title" type="text" placeholder="Step name" @input="changed" />
        <button type="button" class="workflow-icon-btn" title="Move up" :disabled="index === 0" @click="move(index, -1)">↑</button>
        <button type="button" class="workflow-icon-btn" title="Move down" :disabled="index === steps.length - 1" @click="move(index, 1)">↓</button>
        <button type="button" class="workflow-icon-btn workflow-icon-btn-danger" title="Remove step" @click="remove(index)">×</button>
      </header>

      <div v-if="step.type === 'modify_record'" class="workflow-step-body">
        <div class="workflow-settings">
          <label class="workflow-field">
            Record
          <select v-model="step.target" class="workflow-select" @change="changed">
            <option value="trigger">Starting record</option>
            <option value="related">A related record</option>
            <option value="variable">Record in a variable</option>
          </select>
        </label>
        <label v-if="step.target === 'variable'" class="workflow-field">
          Variable
          <select v-model="step.recordVariable" class="workflow-select" @change="changed">
            <option value="">Choose a variable</option>
            <option v-for="name in variableNames" :key="name" :value="name">{{ name }}</option>
          </select>
        </label>
          <label v-if="step.target === 'related'" class="workflow-field">
            Relationship
            <select :value="relationshipKey(step)" class="workflow-select" @change="setRelationship(step, $event.target.value)">
              <option value="">Choose a relationship</option>
              <option v-for="target in relationshipTargets" :key="target.key" :value="target.key">{{ target.label }}</option>
            </select>
          </label>
        </div>
        <p v-if="step.target === 'related'" class="workflow-hint">Every linked record is updated, up to {{ limits.relatedWrites }}.</p>
        <div v-for="(assignment, assignmentIndex) in step.values" :key="assignmentIndex" class="workflow-row">
          <select v-model="assignment.field" class="workflow-select" @change="changed">
            <option value="">Field</option>
            <option v-for="column in writableFields(targetSheet(step))" :key="column.uniqueTitle" :value="column.uniqueTitle">{{ column.title || column.uniqueTitle }}</option>
          </select>
          <select v-model="assignment.valueSource" class="workflow-select" @change="changed">
            <option value="static">Fixed value</option>
            <option value="field">Starting record</option>
            <option value="variable">Variable</option>
          </select>
          <input v-if="assignment.valueSource === 'static'" v-model="assignment.staticValue" class="workflow-input" type="text" placeholder="Value" @input="changed" />
          <select v-else-if="assignment.valueSource === 'field'" v-model="assignment.sourceField" class="workflow-select" @change="changed">
            <option value="">Field</option>
            <option v-for="column in recordFields" :key="column.uniqueTitle" :value="column.uniqueTitle">{{ column.title || column.uniqueTitle }}</option>
          </select>
          <template v-else>
            <select v-model="assignment.variableName" class="workflow-select" @change="changed">
              <option value="">Variable</option>
              <option v-for="name in variableNames" :key="name" :value="name">{{ name }}</option>
            </select>
            <input v-model="assignment.variableField" class="workflow-input" type="text" placeholder="field name" @input="changed" />
          </template>
          <button type="button" class="workflow-icon-btn" title="Remove field" @click="removeAssignment(step, assignmentIndex)">×</button>
        </div>
        <button type="button" class="workflow-text-btn" @click="addAssignment(step)">Add field</button>
      </div>

      <div v-else-if="step.type === 'query'" class="workflow-step-body">
        <div class="workflow-settings">
          <label class="workflow-field">
            Spreadsheet
            <select v-model="step.spreadSheetUniqueTitle" class="workflow-select" :disabled="step.relatedToTrigger" @change="changed">
              <option value="">Choose a spreadsheet</option>
              <option v-for="sheet in spreadsheets" :key="sheet.uniqueTitle" :value="sheet.uniqueTitle">{{ sheet.title || sheet.uniqueTitle }}</option>
            </select>
          </label>
          <label class="workflow-field">
            Store as
            <select v-model="step.resultMode" class="workflow-select" @change="changed">
              <option value="first">First record</option>
              <option value="count">Count</option>
              <option value="list">List</option>
            </select>
          </label>
          <label v-if="step.resultMode === 'list'" class="workflow-field">
            Maximum
            <input :value="step.maxRows == null ? '' : step.maxRows" class="workflow-input workflow-max" type="number" min="1" placeholder="all" @input="setOptionalMax(step, 'maxRows', $event.target.value)" />
          </label>
          <label class="workflow-field">
            Variable name
            <input v-model="step.variableName" class="workflow-input" type="text" placeholder="variable_name" @input="changed" />
          </label>
        </div>
        <label class="workflow-check">
          <input v-model="step.relatedToTrigger" type="checkbox" @change="onRelatedQueryChange(step)" />
          Only records linked to the starting record
        </label>
        <label v-if="step.relatedToTrigger" class="workflow-field">
          Relationship
          <select :value="relationshipKey(step)" class="workflow-select" @change="setQueryRelationship(step, $event.target.value)">
            <option value="">Choose a relationship</option>
            <option v-for="target in relationshipTargets" :key="target.key" :value="target.key">{{ target.label }}</option>
          </select>
        </label>
        <p v-if="!step.spreadSheetUniqueTitle" class="workflow-hint">Choose a spreadsheet to filter its fields.</p>
        <div class="workflow-filter-head">
          <span>Filters</span>
          <select v-model="step.filterMatchMode" class="workflow-select" @change="changed">
            <option value="all">Match all</option>
            <option value="any">Match any</option>
          </select>
        </div>
        <div v-for="(filter, filterIndex) in step.filters" :key="filterIndex" class="workflow-row">
          <select v-model="filter.field" class="workflow-select" @change="onFilterField(step, filter)">
            <option value="">Field</option>
            <option v-for="column in recordFieldsFor(step.spreadSheetUniqueTitle)" :key="column.uniqueTitle" :value="column.uniqueTitle">{{ column.title || column.uniqueTitle }}</option>
          </select>
          <select v-model="filter.operator" class="workflow-select" @change="changed">
            <option v-for="operator in queryOperators" :key="operator.value" :value="operator.value">{{ operator.label }}</option>
          </select>
          <template v-if="filter.operator !== 'empty' && filter.operator !== 'notEmpty'">
            <select v-model="filter.valueSource" class="workflow-select" @change="changed">
              <option value="static">Fixed value</option>
              <option value="field">Starting record</option>
              <option value="variable">Variable</option>
            </select>
            <input v-if="filter.valueSource === 'static'" v-model="filter.staticValue" class="workflow-input" type="text" placeholder="Value" @input="changed" />
            <select v-else-if="filter.valueSource === 'field'" v-model="filter.sourceField" class="workflow-select" @change="changed">
              <option value="">Field</option>
              <option v-for="column in recordFields" :key="column.uniqueTitle" :value="column.uniqueTitle">{{ column.title || column.uniqueTitle }}</option>
            </select>
            <select v-else v-model="filter.variableName" class="workflow-select" @change="changed">
              <option value="">Variable</option>
              <option v-for="name in variableNames" :key="name" :value="name">{{ name }}</option>
            </select>
          </template>
          <button type="button" class="workflow-icon-btn" title="Remove filter" @click="removeFilter(step, filterIndex)">×</button>
        </div>
        <button type="button" class="workflow-text-btn" @click="addFilter(step)">Add filter</button>
      </div>

      <div v-else-if="step.type === 'trigger_automation'" class="workflow-step-body">
        <div class="workflow-settings">
          <label class="workflow-field">
            Automation
            <select v-model="step.automationUniqueTitle" class="workflow-select" @change="onAutomationChange(step)">
              <option value="">Choose an automation</option>
              <option v-for="automation in automations" :key="automation.uniqueTitle" :value="automation.uniqueTitle">{{ automation.title || automation.uniqueTitle }}</option>
            </select>
          </label>
          <label class="workflow-field">
            Record
            <select v-model="step.recordSource" class="workflow-select" @change="changed">
              <option value="trigger">Starting record</option>
              <option value="variable">Record in a variable</option>
            </select>
          </label>
          <label v-if="step.recordSource === 'variable'" class="workflow-field">
            Variable
            <select v-model="step.recordVariable" class="workflow-select" @change="changed">
              <option value="">Choose a variable</option>
              <option v-for="name in variableNames" :key="name" :value="name">{{ name }}</option>
            </select>
          </label>
        </div>
        <div v-for="(mapping, mappingIndex) in step.variables" :key="mappingIndex" class="workflow-row">
          <input v-model="mapping.name" class="workflow-input" type="text" placeholder="Automation variable" @input="changed" />
          <select v-model="mapping.valueSource" class="workflow-select" @change="changed">
            <option value="field">Starting record</option>
            <option value="variable">Variable</option>
            <option value="static">Fixed value</option>
          </select>
          <select v-if="mapping.valueSource === 'field'" v-model="mapping.sourceField" class="workflow-select" @change="changed">
            <option value="">Field</option>
            <option v-for="column in recordFields" :key="column.uniqueTitle" :value="column.uniqueTitle">{{ column.title || column.uniqueTitle }}</option>
          </select>
          <template v-else-if="mapping.valueSource === 'variable'">
            <select v-model="mapping.variableName" class="workflow-select" @change="changed">
              <option value="">Variable</option>
              <option v-for="name in variableNames" :key="name" :value="name">{{ name }}</option>
            </select>
            <input v-model="mapping.variableField" class="workflow-input" type="text" placeholder="field name" @input="changed" />
          </template>
          <input v-else v-model="mapping.staticValue" class="workflow-input" type="text" placeholder="Value" @input="changed" />
          <button type="button" class="workflow-icon-btn" title="Remove variable" @click="removeMapping(step, mappingIndex)">×</button>
        </div>
        <button type="button" class="workflow-text-btn" @click="addMapping(step)">Add variable</button>
      </div>

      <div v-else-if="step.type === 'create_record'" class="workflow-step-body">
        <div class="workflow-settings">
          <label class="workflow-field">
            Spreadsheet
            <select v-model="step.spreadSheetUniqueTitle" class="workflow-select" @change="changed">
              <option value="">Choose a spreadsheet</option>
              <option v-for="sheet in spreadsheets" :key="sheet.uniqueTitle" :value="sheet.uniqueTitle">{{ sheet.title || sheet.uniqueTitle }}</option>
            </select>
          </label>
          <label class="workflow-field">
            Save as variable
            <input v-model="step.variableName" class="workflow-input" type="text" placeholder="variable_name" @input="changed" />
          </label>
        </div>
        <label class="workflow-check">
          <input v-model="step.linkToTrigger" type="checkbox" @change="changed" />
          Link it to the starting record
        </label>
        <label v-if="step.linkToTrigger" class="workflow-field">
          Relationship
          <select :value="relationshipKey(step)" class="workflow-select" @change="setRelationship(step, $event.target.value)">
            <option value="">Choose a relationship</option>
            <option v-for="target in relationshipTargets" :key="target.key" :value="target.key">{{ target.label }}</option>
          </select>
        </label>
        <div v-for="(assignment, assignmentIndex) in step.values" :key="assignmentIndex" class="workflow-row">
          <select v-model="assignment.field" class="workflow-select" @change="changed">
            <option value="">Field</option>
            <option v-for="column in writableFields(step.spreadSheetUniqueTitle)" :key="column.uniqueTitle" :value="column.uniqueTitle">{{ column.title || column.uniqueTitle }}</option>
          </select>
          <select v-model="assignment.valueSource" class="workflow-select" @change="changed">
            <option value="static">Fixed value</option>
            <option value="field">Starting record</option>
            <option value="variable">Variable</option>
          </select>
          <input v-if="assignment.valueSource === 'static'" v-model="assignment.staticValue" class="workflow-input" type="text" placeholder="Value" @input="changed" />
          <select v-else-if="assignment.valueSource === 'field'" v-model="assignment.sourceField" class="workflow-select" @change="changed">
            <option value="">Field</option>
            <option v-for="column in recordFields" :key="column.uniqueTitle" :value="column.uniqueTitle">{{ column.title || column.uniqueTitle }}</option>
          </select>
          <template v-else>
            <select v-model="assignment.variableName" class="workflow-select" @change="changed">
              <option value="">Variable</option>
              <option v-for="name in variableNames" :key="name" :value="name">{{ name }}</option>
            </select>
            <input v-model="assignment.variableField" class="workflow-input" type="text" placeholder="field name" @input="changed" />
          </template>
          <button type="button" class="workflow-icon-btn" title="Remove field" @click="removeAssignment(step, assignmentIndex)">×</button>
        </div>
        <button type="button" class="workflow-text-btn" @click="addAssignment(step)">Add field</button>
      </div>

      <div v-else-if="step.type === 'set_variable'" class="workflow-step-body">
        <div class="workflow-settings">
          <label class="workflow-field">
            Variable name
            <input v-model="step.variableName" class="workflow-input" type="text" placeholder="variable_name" @input="changed" />
          </label>
          <label class="workflow-field">
            Value
            <select v-model="step.valueSource" class="workflow-select" @change="changed">
              <option value="static">Fixed value</option>
              <option value="field">Starting record</option>
              <option value="variable">Variable</option>
            </select>
          </label>
          <input v-if="step.valueSource === 'static'" v-model="step.staticValue" class="workflow-input" type="text" placeholder="Value" @input="changed" />
          <select v-else-if="step.valueSource === 'field'" v-model="step.sourceField" class="workflow-select" @change="changed">
            <option value="">Field</option>
            <option v-for="column in recordFields" :key="column.uniqueTitle" :value="column.uniqueTitle">{{ column.title || column.uniqueTitle }}</option>
          </select>
          <template v-else>
            <select v-model="step.sourceVariable" class="workflow-select" @change="changed">
              <option value="">Variable</option>
              <option v-for="name in variableNames" :key="name" :value="name">{{ name }}</option>
            </select>
            <input v-model="step.sourceVariableField" class="workflow-input" type="text" placeholder="field name" @input="changed" />
          </template>
        </div>
      </div>

      <div v-else-if="step.type === 'for_each'" class="workflow-step-body">
        <div class="workflow-settings">
          <label class="workflow-field">
            List
            <select v-model="step.listVariable" class="workflow-select" @change="changed">
              <option value="">Choose a variable</option>
              <option v-for="name in variableNames" :key="name" :value="name">{{ name }}</option>
            </select>
          </label>
          <label class="workflow-field">
            Current record name
            <input v-model="step.itemVariable" class="workflow-input" type="text" placeholder="item" @input="changed" />
          </label>
          <label class="workflow-field">
            Maximum
            <input :value="step.maxItems == null ? '' : step.maxItems" class="workflow-input workflow-max" type="number" min="1" placeholder="all" @input="setOptionalMax(step, 'maxItems', $event.target.value)" />
          </label>
        </div>
        <p class="workflow-hint">Leave maximum empty to run once for every record in the list.</p>
        <workflow-steps
          :steps="step.steps"
          :spreadsheets="spreadsheets"
          :automations="automations"
          :variable-names="variableNames"
          :workflow-sheet="workflowSheet"
          :depth="depth + 1"
          @change="changed"
        />
      </div>

      <div v-else-if="step.type === 'stop'" class="workflow-step-body">
        <label class="workflow-field">
          Message
          <input v-model="step.message" class="workflow-input" type="text" placeholder="Why this run stops" @input="changed" />
        </label>
        <p class="workflow-hint">Steps after this one do not run.</p>
      </div>

      <div v-else-if="step.type === 'branch'" class="workflow-step-body">
        <p class="workflow-hint">The first matching group runs. Later steps continue after the whole branch.</p>
        <section v-for="(branch, branchIndex) in step.branches" :key="branch.id || branchIndex" class="workflow-branch" :class="'workflow-branch-' + branch.kind">
          <header class="workflow-branch-head">
            <strong>{{ branchTitle(branch, branchIndex) }}</strong>
            <input v-model="branch.label" class="workflow-input" type="text" placeholder="Group name" @input="changed" />
            <select v-if="branch.kind !== 'else'" v-model="branch.match" class="workflow-select" @change="changed">
              <option value="all">All conditions</option>
              <option value="any">Any condition</option>
            </select>
            <button v-if="branch.kind !== 'if'" type="button" class="workflow-icon-btn" title="Remove group" @click="removeBranch(step, branchIndex)">×</button>
          </header>
          <div v-if="branch.kind !== 'else'">
            <div v-for="(condition, conditionIndex) in branch.conditions" :key="conditionIndex" class="workflow-row">
              <select v-model="condition.source" class="workflow-select" @change="changed">
                <option value="field">Starting record</option>
                <option value="variable">Variable</option>
              </select>
              <select v-if="condition.source === 'field'" v-model="condition.field" class="workflow-select" @change="onConditionField(condition)">
                <option value="">Field</option>
                <option v-for="column in recordFields" :key="column.uniqueTitle" :value="column.uniqueTitle">{{ column.title || column.uniqueTitle }}</option>
              </select>
              <template v-else>
                <select v-model="condition.variableName" class="workflow-select" @change="changed">
                  <option value="">Variable</option>
                  <option v-for="name in variableNames" :key="name" :value="name">{{ name }}</option>
                </select>
                <input v-model="condition.variableField" class="workflow-input" type="text" placeholder="field name" @input="changed" />
              </template>
              <select v-model="condition.operator" class="workflow-select" @change="changed">
                <option v-for="operator in operatorsFor(condition)" :key="operator.key" :value="operator.key">{{ operator.label }}</option>
              </select>
              <template v-if="condition.operator !== 'empty' && condition.operator !== 'notEmpty'">
                <select v-model="condition.valueSource" class="workflow-select" @change="changed">
                  <option value="static">Fixed value</option>
                  <option value="field">Starting record</option>
                  <option value="variable">Variable</option>
                </select>
                <input v-if="condition.valueSource !== 'field' && condition.valueSource !== 'variable'" v-model="condition.staticValue" class="workflow-input" type="text" placeholder="Value" @input="changed" />
                <select v-else-if="condition.valueSource === 'field'" v-model="condition.sourceField" class="workflow-select" @change="changed">
                  <option value="">Field</option>
                  <option v-for="column in recordFields" :key="column.uniqueTitle" :value="column.uniqueTitle">{{ column.title || column.uniqueTitle }}</option>
                </select>
                <select v-else v-model="condition.compareVariable" class="workflow-select" @change="changed">
                  <option value="">Variable</option>
                  <option v-for="name in variableNames" :key="name" :value="name">{{ name }}</option>
                </select>
              </template>
              <button type="button" class="workflow-icon-btn" title="Remove condition" @click="removeCondition(branch, conditionIndex)">×</button>
            </div>
            <button type="button" class="workflow-text-btn" @click="addCondition(branch)">Add condition</button>
          </div>
          <workflow-steps
            :steps="branch.steps"
            :spreadsheets="spreadsheets"
            :automations="automations"
            :variable-names="variableNames"
            :workflow-sheet="workflowSheet"
            :depth="depth + 1"
            @change="changed"
          />
        </section>
        <button type="button" class="workflow-text-btn" @click="addElseIf(step)">Add else if group</button>
      </div>
    </article>
    <div v-if="depth < limits.depth" class="workflow-add">
      <button type="button" class="workflow-text-btn" @click="add('modify_record')">Add record update</button>
      <button type="button" class="workflow-text-btn" @click="add('create_record')">Add create record</button>
      <button type="button" class="workflow-text-btn" @click="add('query')">Add query</button>
      <button type="button" class="workflow-text-btn" @click="add('set_variable')">Add variable</button>
      <button type="button" class="workflow-text-btn" @click="add('for_each')">Add for each</button>
      <button type="button" class="workflow-text-btn" @click="add('trigger_automation')">Add automation</button>
      <button type="button" class="workflow-text-btn" @click="add('branch')">Add branch</button>
      <button type="button" class="workflow-text-btn" @click="add('stop')">Add stop</button>
    </div>
  </div>
</template>

<script>
import { operatorsForDataType } from '../../../../shared/display-rules.js'
import { automationVariableNames, writableColumns } from '../../../../shared/automation-integration.js'
import { relationshipWriteTargets } from '../../../../shared/submission-record-actions.js'
import {
  LIMITS,
  QUERY_OPERATORS,
  blankAssignment,
  blankBranch,
  blankCondition,
  blankFilter,
  blankStep
} from '../../../../shared/step-workflow.js'

const SYSTEM_FIELDS = [
  { uniqueTitle: 'id', title: 'ID', type: 'number' },
  { uniqueTitle: 'createdAt', title: 'Created', type: 'dateTime' },
  { uniqueTitle: 'updatedAt', title: 'Updated', type: 'dateTime' }
]

const QUERY_LABELS = {
  '=': 'equals',
  '!=': 'does not equal',
  '<': 'less than',
  '<=': 'less than or equal',
  '>': 'greater than',
  '>=': 'greater than or equal',
  contains: 'contains',
  begins: 'starts with',
  ends: 'ends with',
  empty: 'is blank',
  notEmpty: 'is not blank'
}

export default {
  name: 'WorkflowSteps',
  props: {
    steps: { type: Array, required: true },
    spreadsheets: { type: Array, default: () => [] },
    automations: { type: Array, default: () => [] },
    variableNames: { type: Array, default: () => [] },
    workflowSheet: { type: String, default: '' },
    depth: { type: Number, default: 0 }
  },
  data () {
    return {
      limits: LIMITS,
      stepTypes: [
        { value: 'modify_record', label: 'Update a record' },
        { value: 'create_record', label: 'Create a record' },
        { value: 'query', label: 'Query' },
        { value: 'set_variable', label: 'Set a variable' },
        { value: 'for_each', label: 'For each record' },
        { value: 'trigger_automation', label: 'Run an automation' },
        { value: 'branch', label: 'Branch' },
        { value: 'stop', label: 'Stop' }
      ],
      queryOperators: QUERY_OPERATORS.map((value) => ({ value, label: QUERY_LABELS[value] || value }))
    }
  },
  computed: {
    workflowSpreadsheet () {
      return (this.spreadsheets || []).find((sheet) => sheet.uniqueTitle === this.workflowSheet) || null
    },
    relationshipTargets () {
      return relationshipWriteTargets(this.workflowSpreadsheet, this.spreadsheets)
    },
    recordFields () {
      return this.recordFieldsFor(this.workflowSheet)
    }
  },
  methods: {
    changed () {
      this.$emit('change')
    },
    setOptionalMax (step, key, value) {
      const number = parseInt(value, 10)
      step[key] = number > 0 ? number : null
      this.changed()
    },
    sheetByTitle (uniqueTitle) {
      return (this.spreadsheets || []).find((sheet) => sheet.uniqueTitle === uniqueTitle) || null
    },
    recordFieldsFor (uniqueTitle) {
      const sheet = this.sheetByTitle(uniqueTitle)
      return SYSTEM_FIELDS.concat((sheet && sheet.columns) || [])
    },
    writableFields (uniqueTitle) {
      return writableColumns((this.sheetByTitle(uniqueTitle) || {}).columns)
    },
    targetSheet (step) {
      if (!step || step.target !== 'related') return this.workflowSheet
      return this.relationshipTarget(step).relatedSpreadSheet || ''
    },
    relationshipKey (step) {
      if (!step) return ''
      if (step.relatedSource === 'reverse' && step.sourceSpreadsheet && step.sourceRelationshipColumn) {
        return `reverse:${step.sourceSpreadsheet}:${step.sourceRelationshipColumn}`
      }
      if (step.relationshipColumn) return `forward:${step.relationshipColumn}`
      return ''
    },
    relationshipTarget (step) {
      return this.relationshipTargets.find((target) => target.key === this.relationshipKey(step)) || {}
    },
    setRelationship (step, key) {
      const target = this.relationshipTargets.find((item) => item.key === key)
      if (!target) return
      step.relatedSource = target.relatedSource
      step.relationshipColumn = target.relationshipColumn || ''
      step.sourceSpreadsheet = target.sourceSpreadsheet || ''
      step.sourceRelationshipColumn = target.sourceRelationshipColumn || ''
      this.changed()
    },
    setQueryRelationship (step, key) {
      this.setRelationship(step, key)
      const target = this.relationshipTarget(step)
      if (target.relatedSpreadSheet) step.spreadSheetUniqueTitle = target.relatedSpreadSheet
      this.changed()
    },
    onRelatedQueryChange (step) {
      if (step.relatedToTrigger) {
        const target = this.relationshipTarget(step)
        if (target.relatedSpreadSheet) step.spreadSheetUniqueTitle = target.relatedSpreadSheet
      }
      this.changed()
    },
    onTypeChange (step) {
      const fresh = blankStep(step.type)
      fresh.id = step.id
      fresh.title = step.title
      Object.keys(step).forEach((key) => { delete step[key] })
      Object.assign(step, fresh)
      this.changed()
    },
    add (type) {
      this.steps.push(blankStep(type))
      this.changed()
    },
    remove (index) {
      this.steps.splice(index, 1)
      this.changed()
    },
    move (index, direction) {
      const next = index + direction
      const step = this.steps[index]
      this.steps.splice(index, 1)
      this.steps.splice(next, 0, step)
      this.changed()
    },
    addAssignment (step) {
      if (!Array.isArray(step.values)) step.values = []
      step.values.push(blankAssignment())
      this.changed()
    },
    removeAssignment (step, index) {
      step.values.splice(index, 1)
      this.changed()
    },
    addFilter (step) {
      if (!Array.isArray(step.filters)) step.filters = []
      step.filters.push(blankFilter())
      this.changed()
    },
    removeFilter (step, index) {
      step.filters.splice(index, 1)
      this.changed()
    },
    onFilterField (step, filter) {
      const column = this.recordFieldsFor(step.spreadSheetUniqueTitle).find((item) => item.uniqueTitle === filter.field)
      filter.dataType = column && (column.type === 'number' || column.type === 'duration' || column.type === 'sequence') ? 'number' : 'text'
      this.changed()
    },
    onAutomationChange (step) {
      const automation = (this.automations || []).find((item) => item.uniqueTitle === step.automationUniqueTitle)
      if (automation && (!step.variables || !step.variables.length)) {
        step.variables = automationVariableNames(automation).map((item) => ({
          name: item.variableName,
          valueSource: 'field',
          staticValue: '',
          sourceField: '',
          variableName: '',
          variableField: ''
        }))
      }
      this.changed()
    },
    addMapping (step) {
      if (!Array.isArray(step.variables)) step.variables = []
      step.variables.push({
        name: '',
        valueSource: 'field',
        staticValue: '',
        sourceField: '',
        variableName: '',
        variableField: ''
      })
      this.changed()
    },
    removeMapping (step, index) {
      step.variables.splice(index, 1)
      this.changed()
    },
    operatorsFor (condition) {
      return operatorsForDataType(condition.dataType || 'string')
    },
    onConditionField (condition) {
      const column = this.recordFields.find((item) => item.uniqueTitle === condition.field)
      const typeMap = { number: 'number', dateTime: 'datetime', relationship: 'relationship', duration: 'number', sequence: 'number', signature: 'signature' }
      condition.dataType = column ? (typeMap[column.type] || 'string') : 'string'
      const operators = this.operatorsFor(condition)
      if (!operators.some((operator) => operator.key === condition.operator)) {
        condition.operator = operators[0] ? operators[0].key : '='
      }
      this.changed()
    },
    addCondition (branch) {
      if (!Array.isArray(branch.conditions)) branch.conditions = []
      branch.conditions.push(blankCondition())
      this.changed()
    },
    removeCondition (branch, index) {
      branch.conditions.splice(index, 1)
      this.changed()
    },
    branchTitle (branch, index) {
      if (branch.kind === 'else') return 'Else'
      if (index === 0) return 'If'
      return 'Else if'
    },
    addElseIf (step) {
      if (!Array.isArray(step.branches)) step.branches = []
      const branch = blankBranch('elseif')
      const elseIndex = step.branches.findIndex((item) => item.kind === 'else')
      if (elseIndex === -1) step.branches.push(branch)
      else step.branches.splice(elseIndex, 0, branch)
      this.changed()
    },
    removeBranch (step, index) {
      step.branches.splice(index, 1)
      this.changed()
    }
  }
}
</script>

<style scoped>
.workflow-steps {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.workflow-empty,
.workflow-hint {
  color: #8d8d8a;
  font-size: 0.82rem;
  margin: 0;
}
.workflow-step {
  border: 1px solid #2e2e2b;
  border-radius: 8px;
  background: #141412;
  padding: 10px;
}
.workflow-step-head,
.workflow-branch-head,
.workflow-row,
.workflow-add,
.workflow-filter-head {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}
.workflow-row,
.workflow-branch-head,
.workflow-filter-head {
  align-items: flex-end;
}
.workflow-step-head {
  margin-bottom: 8px;
}
.workflow-step-index {
  width: 1.4rem;
  color: #9ad12b;
  font-weight: 700;
}
.workflow-step-body {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-left: 1.6rem;
}
.workflow-settings {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 14px;
  align-items: flex-end;
}
.workflow-field {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 3px;
  flex: 0 0 auto;
  color: #b0b0ae;
  font-size: 0.72rem;
}
.workflow-select,
.workflow-input {
  height: 30px;
  width: 11rem;
  max-width: 100%;
  box-sizing: border-box;
  background: #1c1c19;
  color: #e8e8e6;
  border: 1px solid #3a3a36;
  border-radius: 4px;
  padding: 2px 8px;
  font-size: 0.82rem;
}
.workflow-input.workflow-max {
  width: 6rem;
}
.workflow-step-title {
  flex: 1 1 10rem;
  width: auto;
  min-width: 8rem;
  max-width: 24rem;
}
.workflow-row .workflow-select,
.workflow-row .workflow-input,
.workflow-branch-head .workflow-select,
.workflow-branch-head .workflow-input,
.workflow-filter-head .workflow-select {
  width: auto;
  min-width: 7.5rem;
  max-width: 14rem;
}
.workflow-check {
  color: #c8c8c4;
  font-size: 0.85rem;
}
.workflow-icon-btn,
.workflow-text-btn {
  background: transparent;
  color: #c8c8c4;
  border: 1px solid #3a3a36;
  border-radius: 4px;
  cursor: pointer;
}
.workflow-icon-btn {
  flex: 0 0 28px;
  width: 28px;
  height: 28px;
}
.workflow-text-btn {
  padding: 4px 8px;
  font-size: 0.8rem;
}
.workflow-icon-btn-danger {
  color: #e07a7a;
}
.workflow-branch {
  border: 1px solid #3a3a36;
  border-left: 3px solid #9ad12b;
  border-radius: 6px;
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  background: #10110f;
}
.workflow-branch-elseif {
  border-left-color: #d2b48c;
}
.workflow-branch-else {
  border-left-color: #8d8d8a;
}
.workflow-branch-head strong {
  min-width: 4.5rem;
  color: #e8e8e6;
}
</style>
